import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import { addScanToHistory } from '@/lib/scan-store';
import { API_CONFIG } from '@/shared/constants/api';
import HttpService from '@/shared/services/http.service';
import { Button } from '@/components/ui/button';

const InboundFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const captureImage = searchParams.get('imageUrl');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null); // Clear previous errors

    try {
      // Call POST API for inbound flow
      const response = await HttpService.post(API_CONFIG.path.inboundFlow, {
        imageUrl: captureImage,
        // ...(barcodes && barcodes.length > 0 && { barcodes }),
      });
      const packageId = response?.packageId;
      const packageNumber = response?.packageNumber;

      if (packageId) {
        addScanToHistory({
          trackingId: packageId,
          slot: packageId, // Use packageId as slot for now
          status: 'stored',
          type: 'inbound',
        });

        setTimeout(() => {
          navigate(`/slot-result?packageId=${packageId}&packageNumber=${encodeURIComponent(packageNumber || '')}`);
        }, 200);
      } else {
        throw new Error(t('no_package_id_returned'));
      }
      setSubmitting(false);
    } catch (err) {
      console.error(err);

      // Handle specific error cases with user-friendly messages
      let errorMessage = t('failed_to_store_package');

      if (err.message?.includes('409') || err.status === 409 || err.response?.status === 409) {
        errorMessage = t('parcel_already_processed');
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message && !err.message.includes('status code')) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-73px)] bg-background">
      <header className="flex items-center justify-between px-4 py-2 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-foreground font-semibold text-lg">{t('receive_parcel')}</h1>
          <p className="text-muted-foreground text-sm">{t('step_2_review_submit')}</p>
        </div>
        <div className="w-10" />
      </header>

      <div className="max-w-lg mx-auto space-y-6  p-6">
        {captureImage && (
          <div className="action-card p-3">
            <img src={captureImage} alt="Invoice" className="w-full rounded-lg object-cover max-h-64" />
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full bg-gray-100 hover:bg-gray-200"
              disabled={submitting}
              onClick={() => navigate('/scan?mode=inbound ')}
            >
              {t('retake_photo')}
            </Button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-red-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-red-800 font-medium">{t('error')}</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          variant="action"
          size="xxl"
          className="w-full"
          onClick={handleSubmit}
          disabled={submitting || !captureImage}
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
              {t('processing')}
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 mr-2" />
              {t('submit_get_slot')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default InboundFlow;
